
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function WiringConditionInspectionsPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Wiring Condition Inspections',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Wiring condition inspections in Fresno, CA. Assess electrical wiring age, condition, and safety. Identify outdated wiring that needs replacement.',
    url: `${siteUrl}/services/testing-certification/wiring-condition-inspections`,
  };

  const features = [
    {
      icon: 'ri-search-eye-line',
      title: 'Comprehensive Wiring Assessment',
      description:
        'Thorough inspection of all accessible wiring throughout your Fresno property',
    },
    {
      icon: 'ri-alert-line',
      title: 'Outdated Wiring Identification',
      description:
        'Identify knob-and-tube, aluminum, cloth-covered, and other outdated wiring types',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Safety Hazard Detection',
      description:
        'Find damaged insulation, exposed wires, improper connections, and fire risks',
    },
    {
      icon: 'ri-flashlight-line',
      title: 'Thermal Imaging',
      description:
        'Infrared scans detect overheating wires and connections behind walls',
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Code Compliance Review',
      description:
        'Verify wiring methods meet California Title 24 and NEC standards',
    },
    {
      icon: 'ri-home-gear-line',
      title: 'Connection Inspection',
      description:
        'Check junction boxes, splices, and connections for proper installation and safety',
    },
    {
      icon: 'ri-tools-line',
      title: 'Grounding Verification',
      description: 'Ensure proper grounding throughout the electrical system',
    },
    {
      icon: 'ri-file-text-line',
      title: 'Detailed Wiring Report',
      description:
        'Comprehensive report with wiring age, condition, hazards, and upgrade recommendations',
    },
  ];

  const whatsIncluded = [
    'Licensed C-10 electrician inspection',
    'Accessible wiring assessment',
    'Wiring type and age identification',
    'Insulation condition evaluation',
    'Connection and splice inspection',
    'Thermal imaging scans',
    'Grounding system verification',
    'Safety hazard identification',
    'Code compliance review',
    'Detailed written report with photos',
  ];

  return (
    <>
      <SEOHead
        title="Wiring Condition Inspections Fresno CA | Electrical Wiring Assessment"
        description="Wiring condition inspections in Fresno, CA. Assess electrical wiring age, condition, and safety. Identify outdated wiring that needs replacement. Licensed electricians."
        canonical="/services/testing-certification/wiring-condition-inspections"
        keywords="wiring condition inspection Fresno, electrical wiring inspection, wiring assessment, outdated wiring inspection Fresno"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Wiring Condition Inspections — Fresno, CA"
        heroSubtitle="Assess your electrical wiring age, condition, and safety"
        heroImage="https://public.readdy.ai/ai/img_res/6c497b7e0cc70d1cb3b1aa9f31c10699.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Close%20up%20of%20electrical%20wiring%20inspection%20showing%20various%20wire%20types%20including%20modern%20romex%20and%20older%20cloth%20covered%20wiring%20with%20inspection%20tools%20and%20thermal%20imaging%20camera%20on%20table%20bright%20professional%20setting&width=700&height=600&seq=wiring-insp-fresno-001&orientation=portrait"
        fresnoContent="Fresno homeowners with older properties need wiring condition inspections to ensure electrical safety. Our licensed electricians assess wiring age, condition, and safety throughout Fresno County — from historic homes in Tower District to older properties in Woodward Park. We identify outdated wiring like knob-and-tube, aluminum wiring, or cloth-covered wiring that creates fire risks. Our thermal imaging detects overheating wires behind walls, and our detailed reports help you plan necessary upgrades to protect your family and property."
        features={features}
        whatsIncluded={whatsIncluded}
        faqs={[
          {
            question: 'Why do I need a wiring condition inspection in Fresno?',
            answer:
              'Wiring condition inspections identify outdated, damaged, or dangerous wiring before it causes fires or electrical failures. Older Fresno homes often have knob-and-tube wiring, aluminum wiring, or cloth-covered wiring that deteriorates over time. Insurance companies may require wiring inspections for older homes, and buyers often request them during real estate transactions. Regular inspections ensure your wiring is safe.',
          },
          {
            question: 'How much does a wiring condition inspection cost in Fresno?',
            answer:
              'Wiring condition inspections in Fresno typically cost $200–$400 depending on home size and accessibility. Homes with difficult-to-access wiring in attics, crawl spaces, or walls may cost more. We provide upfront pricing and detailed reports showing wiring type, age, condition, safety concerns, and upgrade recommendations for your Fresno property.',
          },
          {
            question: 'What types of outdated wiring do you find in Fresno homes?',
            answer:
              "Common outdated wiring in Fresno homes includes knob-and-tube wiring (1880s–1940s), aluminum wiring (1960s–1970s), cloth-covered wiring (1920s–1960s), and early Romex without ground wires. These wiring types create fire risks, don't meet current codes, and often can't handle modern electrical demands. Our inspections identify these outdated systems and provide replacement recommendations.",
          },
          {
            question: 'What does a wiring condition inspection in Fresno include?',
            answer:
              'Our Fresno wiring inspections cover all accessible wiring in attics, crawl spaces, basements, and visible areas. We identify wiring type and age, assess insulation condition, check connections and splices, verify grounding, use thermal imaging to detect overheating, and evaluate code compliance. You receive a detailed report with photos, findings, and recommendations for repairs or rewiring.',
          },
          {
            question: 'Can you inspect wiring behind walls in Fresno homes?',
            answer:
              'We inspect all accessible wiring and use thermal imaging to detect problems behind walls without opening them. Thermal cameras show overheating wires, loose connections, and other issues through walls. For comprehensive assessment of hidden wiring, we may recommend opening sample areas or using specialized detection equipment. Our Fresno electricians minimize invasive inspection methods while ensuring thorough assessment.',
          },
          {
            question: 'How long does a wiring condition inspection take in Fresno?',
            answer:
              'Most residential wiring inspections take 2–4 hours depending on home size, wiring accessibility, and system complexity. Larger homes or properties with extensive attic/crawl space wiring may take longer. We schedule inspections at your convenience and provide detailed reports within 24–48 hours for Fresno homeowners.',
          },
        ]}
      />
    </>
  );
}
